/** Serverzustand der Zeiterfassung. */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createTimeEntry,
  deleteTimeEntry,
  fetchTimeReport,
  listTimeEntries,
  updateTimeEntry,
  type ReportAxis,
  type TimeFilters,
} from './api';

export const timeKeys = {
  all: ['time'] as const,
  entries: (filters: TimeFilters) => ['time', 'entries', filters] as const,
  report: (filters: TimeFilters, groupBy: string) => ['time', 'report', groupBy, filters] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

export function useTimeEntries(filters: TimeFilters, enabled = true) {
  return useQuery({
    queryKey: timeKeys.entries(filters),
    queryFn: () => listTimeEntries(filters),
    enabled,
    retry: retryUnlessPermanent,
  });
}

export function useTimeReport(filters: TimeFilters, groupBy: ReportAxis) {
  return useQuery({
    queryKey: timeKeys.report(filters, groupBy),
    queryFn: () => fetchTimeReport(filters, groupBy),
    retry: retryUnlessPermanent,
  });
}

/**
 * Nach jeder Aenderung wird der ganze Zweig entwertet.
 *
 * Ein Eintrag aendert die Liste **und** jede Auswertung, in der er vorkommt
 * -- und in welcher er vorkommt, haengt an Kunde, Bearbeiter, Tag und
 * Taetigkeitsart. Das in der Oberflaeche nachzubauen hiesse, die Abfrage des
 * Servers ein zweites Mal zu schreiben.
 */
function useTimeMutation<TVariables, TResult>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: timeKeys.all });
    },
  });
}

export function useCreateTimeEntry() {
  return useTimeMutation((payload: Record<string, unknown>) => createTimeEntry(payload));
}

export function useUpdateTimeEntry() {
  return useTimeMutation((variables: { id: string; payload: Record<string, unknown> }) =>
    updateTimeEntry(variables.id, variables.payload),
  );
}

export function useDeleteTimeEntry() {
  return useTimeMutation((entryId: string) => deleteTimeEntry(entryId));
}
